/**
 * Purpose of this view is to show TTMap component with additional interface
 * such as search input, search items and buttons. It is the main view of the app,
 * showing map with option of showing panorama dots (when panorama layer is on)
 * which enable switching to panorama mode.
 */
<template>
    <div id="Map">
        <div class="column is-one-third"
            :class="{
                'map-detail': hasSelection,
                'map-content': !hasSelection,
                'panel-hidden': panelHidden,
            }">
            <div class="content-white">
                <img src="../assets/logo.png" alt="" class="logo">
                <template v-if="hasSelection">
                    <SearchDetail />
                </template>
                <template v-else>
                    <Search />
                </template>
            </div>
        </div>
        <div
            class="column-toggle"
            :class="{ 'panel-hidden': panelHidden }"
            @click="togglePanelHidden">
            <img src="../assets/icon-arrow-left.svg" width="20px" height="40px">
        </div>
        <div class="column column-options">
            <router-link
                class="button settings-button is-secondary is-uppercase is-medium mr-1"
                to="/profile">
                {{ $t("message.settings") }}
            </router-link>
        </div>
        <TTMap
            :apiKey="apiKey"
            :momaURLBuilder="momaURL"
            :markers="rawResults"
            :initialBounds="initialBounds"
            :initialLayer="layer"
            :bounds="selectedBounds"
            :initialStartDate="startDate"
            :initialEndDate="endDate"
            :drawingLngLats="lngLats"
            :drawingMode="isDrawingModeActive"
            :drawingFinished="isDrawingFinished"
            @selectedCoordinates="selectCoordinates"
            @selectMarker="select"
            @center="setCenter"
            @layerChanged="setLayer"
            @trackDateRangeChanged="setTrackDateRange"
            @bounds="setBounds"
            @startDrawingMode="startDrawingMode"
            @endDrawingMode="endDrawingMode"
            @addDrawingPoint="addDrawingPoint"
            @newPoints="setDrawingPoints" />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import Search from '../components/Search.vue';
import SearchDetail from '../components/SearchDetail.vue';
import TTMap from '../components/TTMap.vue';

export default {
    name: 'Map',

    computed: {
        ...mapGetters(['momaURL']),
        ...mapState(['apiKey', 'momaKey']),
        ...mapGetters('map', ['boundsArray', 'startDate', 'endDate']),
        ...mapState('map', ['center', 'layer']),
        ...mapState('panorama', ['panorama']),
        ...mapGetters('permissions', {
            canViewPanoramas: 'panoramasPermission',
        }),
        ...mapGetters('search', ['hasSelection', 'selectedBounds', 'results']),
        ...mapState('search', [
            'query',
            'rawResults',
            'selection',
            'panelHidden',
        ]),
        ...mapState('polygon', [
            'isDrawingModeActive',
            'isDrawingFinished',
            'lngLats',
        ]),

        initialBounds() {
            // Selection bounds take precendence over
            // saved 'ordinary' bounds
            return this.selectedBounds || this.boundsArray;
        },
    },

    methods: {
        ...mapActions('panorama', [
            'hideInformationVisible',
            'clearOrientation',
        ]),
        ...mapActions('search', [
            'setQuery',
            'clearQuery',
            'select',
            'togglePanelHidden',
        ]),
        ...mapActions('map', [
            'selectCoordinates',
            'setCenter',
            'setBounds',
            'setPanoramasVisible',
            'setLayer',
            'setTrackDateRange',
        ]),
        ...mapActions('polygon', [
            'startDrawingMode',
            'endDrawingMode',
            'addDrawingPoint',
            'setDrawingPoints',
        ]),
    },

    components: {
        Search,
        SearchDetail,
        TTMap,
    },

    watch: {
        /**
         * On `panorama` variable change, router is redirected to
         * `panorama` subpage, if the user has correct permision.
         */
        panorama() {
            const {
                $router: { currentRoute: { path } },
                canViewPanoramas,
            } = this;

            if (path === '/panorama' || !canViewPanoramas) {
                // Don't navigate to panorama if we are already there.
                return;
            }

            this.hideInformationVisible();
            this.clearOrientation();
            this.$router.push('/panorama');
        },
    },
};
</script>
